import { getItem } from '@ms/yammer-session-storage';
import { reportError, reportInfo } from '@ms/yammer-telemetry';
import {
  acquireToken,
  doesAuthenticatorSupportImageRenderingCookie,
  getAuthenticator,
  getTokenExpirationTimeInSeconds,
} from '@ms/yammer-web-authenticators';

import { getAdalResourceForYammer, getApiHost, getLegacyHost } from '../domains/session/selectors';
import type { YammerState } from '../state/types';
import type { PromiseThunkAction } from '../types';

import { isLoadedWithEngageDomain } from './isEngageDomain';

const FILES_COOKIELESS_AUTH = 'FilesCookielessAuth';

const getUrl = (state: YammerState) => {
  const shouldUseEngageApiDomain = isLoadedWithEngageDomain();
  const host = shouldUseEngageApiDomain ? getApiHost(state) : getLegacyHost(state);

  return `${host}/api/v1/oauth2/aad_access_token`;
};

const acquireTokenForYammer = (state: YammerState) => {
  const yammerResource = getAdalResourceForYammer(state);

  return acquireToken({ scopes: [yammerResource] });
};

type ClearAuthInCookie = (state: YammerState, token: string) => Promise<Response>;
const clearAuthInCookie: ClearAuthInCookie = (state, token) => {
  const url = getUrl(state);
  const headers = { authorization: `Bearer ${token}` };

  return fetch(url, { headers, credentials: 'include', method: 'DELETE' });
};

type SetAuthTokenInCookie = (state: YammerState, token: string) => Promise<Response>;
export const setAuthTokenInCookie: SetAuthTokenInCookie = (state, token) => {
  const url = getUrl(state);
  const headers: Record<string, string> = {
    authorization: `Bearer ${token}`,
    'X-Yammer-OauthTokenExpiration': 'true',
  };

  if (getItem('Yammer.ThirdPartyCookieBlocked')) {
    headers['X-Yammer-ThirdPartyCookieBlocked'] = 'true';
  }

  return fetch(url, { headers, credentials: 'include' });
};

export const setImageRenderingCookie: () => PromiseThunkAction<void> = (() => {
  let inFlight: boolean;
  let prevToken: string;

  return (): PromiseThunkAction<void> => async (_dispatch, getState) => {
    const authenticator = getAuthenticator();
    if (!doesAuthenticatorSupportImageRenderingCookie(authenticator)) {
      return;
    }

    if (inFlight) {
      return;
    }

    inFlight = true;

    try {
      const startTimeStamp = Date.now();
      const state = getState();

      if (state.ecsExperiments.treatments[FILES_COOKIELESS_AUTH]) {
        return;
      }

      const token = await acquireTokenForYammer(state);
      if (prevToken === token) {
        return;
      }

      const response = await setAuthTokenInCookie(state, token);

      if (!response.ok) {
        throw new Error(`Failed to set image rendering cookie. Received status: ${response.status}`);
      }

      const endTimeStamp = Date.now();

      prevToken = token;

      reportInfo({
        eventName: 'set_image_rendering_cookie_succeeded',
        eventProperties: {
          duration: endTimeStamp - startTimeStamp,
          expiration: getTokenExpirationTimeInSeconds(token),
        },
      });
    } catch (error) {
      reportError({
        error,
        eventProperties: { errorCode: 'SetImageRenderingCookieFailed' },
      });
    } finally {
      inFlight = false;
    }
  };
})();

export const clearImageRenderingCookie: () => PromiseThunkAction<void> = (() => {
  let inFlight: boolean;

  return (): PromiseThunkAction<void> => async (_dispatch, getState) => {
    if (inFlight) {
      return;
    }

    inFlight = true;

    try {
      const state = getState();
      const token = await acquireTokenForYammer(state);

      await clearAuthInCookie(state, token);
    } catch (error) {
      reportError({
        error,
        eventProperties: { errorCode: 'ClearImageRenderingCookieFailed' },
      });
    } finally {
      inFlight = false;
    }
  };
})();
